import React from 'react';
import { Button } from '@/components/ui/button';
import { UserCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSelectedShow } from '@/hooks/useSelectedShow';
import { formatShowDateRange } from '@/lib/showTimezone';

const LOGO_URL = 'https://media.base44.com/images/public/6a1729d95c8303afc7c4b681/791641a40_TradeSyncLogoFinal.png';

/**
 * Top row (logo, profile, new contact) plus the dark event banner. Task:
 * Home screen redesign, agreed with the owner 2026-09-08.
 *
 * Read only, task: remove show switching from Home (2026-09-08). This used
 * to be a themed Select reusing the ShowSelector mechanism, but switching
 * shows from a persistent, always visible control on Home turned out to be
 * too easy to do by accident, and a wrong tap here silently misfiles every
 * contact captured after it. Moving between shows now happens only on the
 * Shows screen (reachable from the More menu), the same place it always
 * required deliberate navigation to reach before this redesign.
 *
 * Three display states, all agreed explicitly rather than guessed:
 *   - a real trade show shows its location and date range
 *   - "All shows" shows a simplified banner with its own explanatory line,
 *     not a blank second row
 *   - a personal bucket also gets a simplified banner, showing the
 *     bucket's own name (e.g. "All other times") rather than a generic
 *     label, plus its own explanatory line, so a rep can tell they are
 *     capturing outside a show rather than into one, and the banner is
 *     never just a large empty dark bar
 */
export default function HomeHeader() {
  const { selectedShow, isAllShows, isLoading } = useSelectedShow();

  const isPersonalBucket = !isAllShows && !!selectedShow?.is_general;
  const simplified = isAllShows || isPersonalBucket;

  const title = isLoading ? 'Loading...' : isAllShows ? 'All shows' : (selectedShow?.name || 'No event selected');
  const range = !simplified && selectedShow ? formatShowDateRange(selectedShow) : null;
  const subtitle = isLoading
    ? null
    : isAllShows
      ? 'Every trade show, combined'
      : isPersonalBucket
        ? 'Personal captures, not tied to a show'
        : selectedShow
          ? [selectedShow.location, range].filter(Boolean).join(' · ')
          : null;

  return (
    <div className="mb-4">
      {/* Top row */}
      <div className="flex items-center justify-between mb-3">
        <img src={LOGO_URL} alt="TradeSync" className="h-9" />
        <div className="flex items-center gap-2">
          {/* Enlarged, task: 2026-09-25, corrected the same day: the first
              attempt put the size on the icon itself (className="w-7 h-7"),
              which had no visible effect. Button's own base classes force
              every icon inside it to a fixed size-4 via a `[&_svg]:size-4`
              descendant selector (button.jsx), which has higher CSS
              specificity than a plain w-* / h-* class on the icon, so the
              icon's own size class could never win regardless of its
              value. The button's own h-11 w-11 (from h-9 w-9) did apply
              correctly, since that is a same-element class conflict
              twMerge resolves fine; only the icon glyph inside was stuck.
              The real fix has to override the same [&_svg]:size-* selector
              on the BUTTON's own className instead, so twMerge treats it as
              the same class group and keeps this one. This is the header's
              own profile icon, between the logo and "+ New Contact", not a
              top-left icon: reported back rather than guessed, since the
              request described it as top-left and this header does not
              have one there. */}
          <Link to="/profile">
            <Button variant="ghost" size="icon" className="h-11 w-11 [&_svg]:size-7 text-muted-foreground" title="Profile">
              <UserCircle />
            </Button>
          </Link>
          <Link to="/new">
            <Button className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-lg min-h-9 px-4 text-xs font-bold tracking-wide">
              + New Contact
            </Button>
          </Link>
        </div>
      </div>

      {/* "Current Show Viewing:" label, task: 2026-09-25. Distinct from the
          Shows admin page's own "Currently Selected Show:" label
          (Shows.jsx), a different page and a different piece of text on
          purpose: this one is what a rep is looking at right now on Home,
          not what the show-picker dropdown on the Shows screen is set to. */}
      <p className="text-base font-bold text-foreground text-center mb-1.5">Current Show Viewing:</p>

      {/* Dark event banner, read only */}
      <div className="bg-slate-900 rounded-xl px-4 py-3">
        <p className="text-sm font-bold text-white truncate">{title}</p>
        {subtitle && <p className="text-xs text-white/70 mt-0.5 truncate">{subtitle}</p>}
      </div>
    </div>
  );
}
